/**
 * Stratiq CMS API client.
 * Replaces the old localStorage layer with calls to FastAPI + MongoDB.
 *
 * Public read:  GET    /api/cms
 * Public write: POST   /api/cms/messages          (contact form)
 * Admin read:   GET    /api/cms          (Bearer token includes messages)
 * Admin write:  PATCH  /api/cms          (per-section update)
 * Admin reset:  DELETE /api/cms
 * Auth:         POST   /api/cms/auth/login        ->  { token, ttlHours }
 *               GET    /api/cms/auth/me           (token validation)
 */

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "stratiq.admin.token";
const CACHE_KEY = "stratiq.cms.cache.v2";  // bumped: bilingual schema

const _bi = (es, en) => ({ es, en });

/**
 * Last-known CMS snapshot used to render the public site immediately while the
 * fresh copy is fetched in the background. Mirrors backend default_cms()
 * (bilingual schema v1).
 */
const FALLBACK_CMS = {
  settings: {
    heroEyebrow:   _bi("AI & Business Advisory", "AI & Business Advisory"),
    heroHeadline:  _bi(
      "Inteligencia que genera ventaja estratégica.",
      "Intelligence that drives strategic advantage.",
    ),
    heroSubheadline: _bi(
      "Automatizamos procesos críticos\ny desarrollamos plataformas inteligentes\npara que tu empresa escale con precisión.",
      "We automate critical processes,\nbuild intelligent platforms and apply AI\nso your company scales with precision and without friction.",
    ),
    aboutParagraph: _bi(
      "Stratiq es una firma boutique de AI & Business Advisory con base en Querétaro, MX y alcance en LATAM y Estados Unidos. Diseñamos e implementamos automatizaciones, plataformas internas y soluciones de inteligencia artificial para empresas que necesitan escalar sin sumar headcount. Cada engagement combina visión estratégica con ejecución técnica medible: salimos de cada proyecto con KPIs claros, procesos documentados y un sistema que tu equipo puede operar.",
      "Stratiq is a boutique AI & Business Advisory firm based in Querétaro, Mexico, serving clients across LATAM and the United States. We design and implement automations, internal platforms and artificial intelligence solutions for companies that need to scale without adding headcount. Every engagement combines strategic vision with measurable technical execution — we leave every project with clear KPIs, documented processes and a system your team can operate.",
    ),
    ctaHeadline: _bi("¿Listo para escalar con IA?", "Ready to scale with AI?"),
    ctaSubtext: _bi(
      "Agenda un diagnóstico gratuito de 30 minutos y descubre el potencial de automatización en tu empresa.",
      "Book a free 30-minute diagnostic call and discover your company's automation potential.",
    ),
    contactEmail: "info@stratiq.com.mx",
    location: "Querétaro, MX",
    responseTime: _bi(
      "Respondemos en menos de 24h hábiles",
      "We reply within 24 business hours",
    ),
  },
  services: [
    { id: "s-ba",
      name:      _bi("Consultoría Business Analyst", "Business Analysis Consulting"),
      shortDesc: _bi("Análisis, mapeo y rediseño de procesos de negocio para maximizar eficiencia operativa.",
                     "Process analysis, mapping and redesign to maximize operational efficiency."),
      icon: "Workflow", visible: true },
    { id: "s-auto",
      name:      _bi("Automatización de Procesos", "Process Automation"),
      shortDesc: _bi("Flujos inteligentes con Make, n8n y Claude API que eliminan tareas repetitivas.",
                     "Intelligent workflows with Make, n8n and Claude API that eliminate repetitive tasks."),
      icon: "Zap", visible: true },
    { id: "s-plat",
      name:      _bi("Desarrollo de Plataformas", "Platform Development"),
      shortDesc: _bi("Sistemas empresariales a medida que centralizan operaciones y datos.",
                     "Custom enterprise systems that centralize operations and data."),
      icon: "LayoutDashboard", visible: true },
    { id: "s-ai",
      name:      _bi("Soluciones AI", "AI Solutions"),
      shortDesc: _bi("Agentes, reportes automáticos y asistentes con inteligencia artificial aplicada al negocio.",
                     "Agents, automated reports and AI-powered assistants applied to business."),
      icon: "Sparkles", visible: true },
    { id: "s-sop",
      name:      _bi("Manuales y Procesos", "Manuals & Processes"),
      shortDesc: _bi("Documentación estructurada, SOPs y diseño de procedimientos operativos.",
                     "Structured documentation, SOPs and operational procedure design."),
      icon: "BookOpen", visible: true },
  ],
  stats: [
    { id: "st1", number: "+340h", suffix: _bi("recuperadas al año por cliente", "recovered per year per client") },
    { id: "st2", number: "12+",   suffix: _bi("módulos empresariales entregados", "enterprise modules delivered") },
    { id: "st3", number: "100%",  suffix: _bi("proyectos con impacto medible", "projects with measurable impact") },
  ],
  cases: [
    { id: "c1",
      industry:    _bi("Marketing Agency", "Marketing Agency"),
      service:     _bi("Automatización de Procesos", "Process Automation"),
      result:      _bi("Reducción del 60% en tiempo de reportes internos",
                       "60% reduction in internal reporting time"),
      description: _bi("Automatización de reportes semanales y consolidación de KPIs en un panel único.",
                       "Automation of weekly reports and consolidation of KPIs in a single panel."),
      status: "published" },
    { id: "c2",
      industry:    _bi("Financial Group", "Financial Group"),
      service:     _bi("Consultoría BA + Plataformas", "BA Consulting + Platforms"),
      result:      _bi("275 subprocesos mapeados, plataforma de 12 módulos en desarrollo",
                       "275 sub-processes mapped, 12-module platform in development"),
      description: _bi("Mapeo de operación end-to-end y diseño arquitectónico de plataforma empresarial.",
                       "End-to-end operational mapping and architectural design of an enterprise platform."),
      status: "published" },
    { id: "c3",
      industry:    _bi("SaaS Startup", "SaaS Startup"),
      service:     _bi("Soluciones AI", "AI Solutions"),
      result:      _bi("Asistente interno entrenado en documentos de la empresa, lanzado en 3 semanas",
                       "Internal assistant trained on company documents, launched in 3 weeks"),
      description: _bi("Agente RAG sobre base de conocimiento interna, integrado al stack de comunicación del equipo.",
                       "RAG agent over internal knowledge base, integrated into the team's communication stack."),
      status: "published" },
  ],
  team: [],
};

function readCache() {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch { return null; }
}
function writeCache(data) {
  try { localStorage.setItem(CACHE_KEY, JSON.stringify(data)); } catch { /* quota */ }
}

let _cache = readCache() || FALLBACK_CMS;
const listeners = new Set();

function notify() {
  for (const fn of listeners) {
    try { fn(_cache); } catch { /* ignore listener error */ }
  }
  window.dispatchEvent(new CustomEvent("stratiq:cms-updated", { detail: _cache }));
}

export function subscribe(callback) {
  listeners.add(callback);
  return () => listeners.delete(callback);
}

// ---------- token ----------
export function getToken() {
  return sessionStorage.getItem(TOKEN_KEY);
}
function setToken(t) {
  if (t) sessionStorage.setItem(TOKEN_KEY, t);
  else sessionStorage.removeItem(TOKEN_KEY);
}
export function isAuthed() {
  return !!getToken();
}
export function adminLogout() {
  setToken(null);
}

function authHeaders() {
  const t = getToken();
  return t ? { Authorization: `Bearer ${t}` } : {};
}

// ---------- fetch helpers ----------
async function safeJson(res) {
  try { return await res.json(); } catch { return null; }
}

async function handle401(res) {
  if (res.status === 401) {
    setToken(null);
    return true;
  }
  return false;
}

// ---------- public API ----------
export async function loadCMS() {
  const res = await fetch(`${API}/cms`, { headers: authHeaders() });
  if (!res.ok) throw new Error("Failed to load CMS");
  _cache = await res.json();
  writeCache(_cache);
  notify();
  return _cache;
}

export function getData() {
  return _cache;
}

export async function addMessage(msg) {
  const res = await fetch(`${API}/cms/messages`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(msg),
  });
  if (!res.ok) {
    const body = await safeJson(res);
    throw new Error(body?.detail || "Failed to send message");
  }
  return safeJson(res);
}

// ---------- admin auth ----------
export async function adminLogin(password) {
  const res = await fetch(`${API}/cms/auth/login`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ password }),
  });
  if (!res.ok) {
    const body = await safeJson(res);
    throw new Error(body?.detail || "Invalid password");
  }
  const { token } = await res.json();
  setToken(token);
  return token;
}

// ---------- admin mutations ----------
async function patchCMS(patch) {
  const res = await fetch(`${API}/cms`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify(patch),
  });
  if (await handle401(res)) {
    throw new Error("Session expired. Please log in again.");
  }
  if (!res.ok) {
    const body = await safeJson(res);
    throw new Error(body?.detail || "Save failed");
  }
  _cache = await res.json();
  writeCache(_cache);
  notify();
  return _cache;
}

export async function updateSection(section, value) {
  return patchCMS({ [section]: value });
}

export async function patchSettings(patchObj) {
  const current = (_cache && _cache.settings) || {};
  return patchCMS({ settings: { ...current, ...patchObj } });
}

export async function resetData() {
  const res = await fetch(`${API}/cms`, {
    method: "DELETE",
    headers: { ...authHeaders() },
  });
  if (await handle401(res)) throw new Error("Session expired.");
  if (!res.ok) throw new Error("Reset failed");
  _cache = await res.json();
  writeCache(_cache);
  notify();
  return _cache;
}
